/*
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * ŚWIEŻOŚĆ DANYCH REFERENCYJNYCH — osobna skala, bez dziedziczenia na sprzedaż
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * Dane referencyjne (szkoły, restauracje, karta) i dane transakcyjne mają osobne źródła i osobne
 * blokery. Świeży słownik szkół NIE czyni świeżymi danych o sprzedaży w szkołach — mimo że oba
 * wiersze na ekranie nazywają się „Szkoły". To jest najłatwiejsze możliwe nieporozumienie na tym
 * ekranie i dlatego skale są rozdzielone typem, a nie tylko opisem.
 *
 * DWA POWODY, DLA KTÓRYCH REFERENCJA BYWA NIEŚWIEŻA — i trzeba je rozróżniać:
 *   1. Dawno nie czytaliśmy źródła (stary `odczytano`).
 *   2. Czytaliśmy dziś, ale ŹRÓDŁO oddało kartę z innego dnia (`dataKarty` ≠ dzień odczytu).
 * Pierwsze to nasza zaległość, drugie to stan po stronie systemu głównego. Zwinięcie ich w jedno
 * „nieświeże" kasuje informację, która wskazuje winowajcę.
 */

export type SwiezoscReferencyjna = 'CURRENT_REFERENCE' | 'STALE_REFERENCE' | 'UNKNOWN_REFERENCE';

export interface WejscieSwiezosci {
  /** Data karty w źródle, w formacie ISO (`YYYY-MM-DD`). null = źródło jej nie podało. */
  dataKarty: string | null;
  /** Moment ODCZYTU (ISO). null = brak zapisanej proweniencji. */
  odczytano: string | null;
  /** Punkt odniesienia. Podawany jawnie, żeby ocena była powtarzalna w teście. */
  teraz: string;
  /** Ile dni od odczytu wolno jeszcze nazywać referencję bieżącą. */
  tolerancjaDni?: number;
}

export interface OcenaSwiezosci {
  stan: SwiezoscReferencyjna;
  /** Zdanie dla właściciela — mówi, DLACZEGO taki stan, a nie tylko jaki. */
  powod: string;
  wiekDni: number | null;
}

const DZIEN = 86_400_000;

function dzien(iso: string): number | null {
  const t = Date.parse(iso);
  return Number.isFinite(t) ? Math.floor(t / DZIEN) : null;
}

export function ocenSwiezoscReferencyjna({
  dataKarty,
  odczytano,
  teraz,
  tolerancjaDni = 2,
}: WejscieSwiezosci): OcenaSwiezosci {
  const dOdczyt = odczytano ? dzien(odczytano) : null;
  const dTeraz = dzien(teraz);

  if (dOdczyt === null || dTeraz === null) {
    return { stan: 'UNKNOWN_REFERENCE', powod: 'Brak zapisanego momentu odczytu źródła.', wiekDni: null };
  }

  const wiek = dTeraz - dOdczyt;

  if (!dataKarty) {
    // Wiek znamy, ale nie wiemy, CO źródło wtedy oddało. To za mało, żeby nazwać referencję bieżącą.
    return {
      stan: 'UNKNOWN_REFERENCE',
      powod: `Odczyt sprzed ${wiek} dni, ale źródło nie podało daty swojej zawartości.`,
      wiekDni: wiek,
    };
  }

  if (wiek > tolerancjaDni) {
    return {
      stan: 'STALE_REFERENCE',
      powod: `Ostatni odczyt sprzed ${wiek} dni — powyżej progu ${tolerancjaDni}. To nasza zaległość, nie stan źródła.`,
      wiekDni: wiek,
    };
  }

  const dKarta = dzien(dataKarty);
  if (dKarta === null) {
    return { stan: 'UNKNOWN_REFERENCE', powod: 'Data zawartości źródła w nierozpoznanym formacie.', wiekDni: wiek };
  }

  if (dKarta !== dOdczyt) {
    const roznica = dOdczyt - dKarta;
    return {
      stan: 'STALE_REFERENCE',
      powod:
        `Odczyt był bieżący, ale źródło oddało zawartość z ${dataKarty} — ${roznica} dni przed odczytem. ` +
        'To stan po stronie systemu głównego, nie nasza zaległość.',
      wiekDni: wiek,
    };
  }

  return {
    stan: 'CURRENT_REFERENCE',
    powod: `Zawartość z ${dataKarty}, odczytana tego samego dnia${wiek > 0 ? `, ${wiek} dni temu` : ''}.`,
    wiekDni: wiek,
  };
}

export const OPIS_SWIEZOSCI: Record<SwiezoscReferencyjna, string> = {
  CURRENT_REFERENCE: 'bieżąca',
  STALE_REFERENCE: 'nieaktualna',
  UNKNOWN_REFERENCE: 'nieustalona',
};
